// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { US_STATES } from '@polarissleep/shared';

export const SUPPORT_EMAIL = 'support@polarissleep.com';

/**
 * States Polaris sees patients in, in the order the state picker lists them.
 * `timeZone` covers most of the state; patients elsewhere in it change it on the address step.
 */
export const SERVED_STATES = [
  { code: 'DC', label: 'Washington, D.C.', shortLabel: 'D.C.', timeZone: 'America/New_York' },
  { code: 'GA', label: 'Georgia', shortLabel: 'Georgia', timeZone: 'America/New_York' },
  { code: 'IN', label: 'Indiana', shortLabel: 'Indiana', timeZone: 'America/New_York' },
  { code: 'MD', label: 'Maryland', shortLabel: 'Maryland', timeZone: 'America/New_York' },
  { code: 'TX', label: 'Texas', shortLabel: 'Texas', timeZone: 'America/Chicago' },
  { code: 'VA', label: 'Virginia', shortLabel: 'Virginia', timeZone: 'America/New_York' },
] as const;

export type ServedStateCode = (typeof SERVED_STATES)[number]['code'];

export const OTHER_STATE = 'other';

const SERVED_STATE_NAMES = new Set<string>(['District of Columbia', ...SERVED_STATES.map((state) => state.label)]);

/** States offered under "Another state": every US state Polaris does not serve. */
export const UNSERVED_STATES: readonly string[] = US_STATES.filter((name) => !SERVED_STATE_NAMES.has(name));

/** "D.C., Georgia, Indiana, Maryland, Texas, and Virginia" */
export const SERVED_STATES_SENTENCE = `${SERVED_STATES.slice(0, -1)
  .map((state) => state.shortLabel)
  .join(', ')}, and ${SERVED_STATES[SERVED_STATES.length - 1].shortLabel}`;

export const MINIMUM_AGE = 3;
export const ADULT_AGE = 18;

/** Parses a complete MM/DD/YYYY date of birth; null if incomplete, not a real date, or in the future. */
export function parseDateOfBirth(value: string | undefined, today: Date = new Date()): Date | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value ?? '');
  if (!match) {
    return null;
  }
  const [month, day, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day || date > today) {
    return null;
  }
  return date;
}

export function ageOn(dateOfBirth: Date, today: Date = new Date()): number {
  const age = today.getFullYear() - dateOfBirth.getFullYear();
  const birthdayPassed =
    today.getMonth() > dateOfBirth.getMonth() ||
    (today.getMonth() === dateOfBirth.getMonth() && today.getDate() >= dateOfBirth.getDate());
  return birthdayPassed ? age : age - 1;
}

/** Age for a typed MM/DD/YYYY date of birth, or null until it is a valid date. */
export function ageFromDateOfBirth(value: string | undefined, today: Date = new Date()): number | null {
  const dateOfBirth = parseDateOfBirth(value, today);
  return dateOfBirth ? ageOn(dateOfBirth, today) : null;
}
